'use client';

import {
  createContext,
  useContext,
  useState,
  PropsWithChildren,
  useEffect,
} from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { isBrowser } from '@onbeam/utils';

export type FormStep<T> = {
  title: string;
  subtitle?: string;
  description?: string;
  fieldNames: (keyof T)[];
};

type FormContextReturn<T extends FieldValues> = {
  activeStep: FormStep<T>;
  steps: FormStep<T>[];
  activeIndex: number;
  onContinue: () => void;
  onPrevious: () => void;
  onNavigate: (stepIndex: number) => void;
  form: UseFormReturn<T>;
  isReviewing: boolean;
};

export type FormProviderProps<T extends z.ZodSchema<any>> = PropsWithChildren<{
  schema: T;
  steps: FormStep<z.TypeOf<T>>[];
  storageKey?: string;
}>;

const FormContext = createContext<FormContextReturn<any> | undefined>(
  undefined,
);

export const FormProvider = <T extends z.ZodSchema<any>>({
  schema,
  steps,
  storageKey,
  children,
}: FormProviderProps<T>) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isReviewing, setReviewing] = useState(false);

  const formMethods = useForm<z.TypeOf<T>>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues:
      storageKey && isBrowser
        ? JSON.parse(localStorage.getItem(storageKey) || '{}')
        : {},
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on unmounting the whole thing
  useEffect(() => {
    const storeState = () => {
      if (!storageKey || !isBrowser) return;
      const values = formMethods.getValues();
      // Clear when no values have been set and on successful submission
      if (
        !Object.values(values).some((value) => !!value) ||
        formMethods.formState.isSubmitSuccessful
      ) {
        return localStorage.removeItem(storageKey);
      }
      localStorage.setItem(storageKey, JSON.stringify(formMethods.getValues()));
    };
    window.addEventListener('beforeunload', storeState);

    return () => {
      window.removeEventListener('beforeunload', storeState);
      storeState();
    };
  }, [storageKey, formMethods.formState.isSubmitSuccessful]);

  const scrollToError = (stepIndex: number) => {
    const firstInvalidField = steps[stepIndex].fieldNames.find(
      (name) => formMethods.formState.errors[name],
    );
    if (!firstInvalidField) return;
    const element = document.querySelector<HTMLInputElement>(
      `[name="${String(firstInvalidField)}"]`,
    );
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const onContinue = async () => {
    if (activeIndex === steps.length - 1) {
      const entireFormIsValid = await formMethods.trigger();
      if (entireFormIsValid) {
        setReviewing(true);
        return;
      }
      // Find the first step with an error and navigate to it
      for (let i = 0; i < steps.length; i++) {
        // @ts-expect-error - We can guarantee the fieldNames matches with the param
        const isValid = await formMethods.trigger(steps[i].fieldNames);
        if (isValid) continue;
        setActiveIndex(i);
        requestAnimationFrame(() => scrollToError(i));
        return;
      }
    }
    // @ts-expect-error - Same as above
    formMethods.trigger(steps[activeIndex].fieldNames).then((isValid) => {
      if (!isValid) return scrollToError(activeIndex);
      setActiveIndex((currentStepIndex) =>
        Math.min(currentStepIndex + 1, steps.length - 1),
      );
    });
  };

  const onPrevious = () => setActiveIndex((prev) => Math.max(prev - 1, 0));

  const onNavigate = (stepIndex: number) => {
    setReviewing(false);
    setActiveIndex(Math.max(Math.min(stepIndex, steps.length - 1), 0));
  };

  return (
    <FormContext.Provider
      value={{
        activeStep: steps[activeIndex],
        onContinue,
        onPrevious,
        onNavigate,
        activeIndex,
        steps,
        form: formMethods,
        isReviewing,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = <
  T extends FieldValues,
>(): FormContextReturn<T> => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context as FormContextReturn<T>;
};
